<script setup lang="ts">
import aboutContent from "~/content/about.json";

const makersStore = useMakersStore();
const reviewsStore = useReviewsStore();
const config = useRuntimeConfig();
const siteOrigin = config.public.mainUrl as string;
const { imageBase, staticUrl } = useImageUrl();

definePageMeta({
  layout: "default",
});

useSeoMeta({
  title: "About Us | Meet the Team Behind London's Best Office Catering",
  description:
    "Discover the story of &Dine-where passion for great food, community, and exceptional service brings people together. Learn how our team delivers quality catering with a personal touch, every day in London's workplaces.",
});

useHead({
  link: [{ rel: "canonical", href: `${siteOrigin}/about` }],
});

onMounted(async () => {
  await Promise.all([makersStore.getMakerCategories(), reviewsStore.getReviews()]);
});
</script>

<template>
  <section class="about">
    <!-- Hero -->
    <div
      class="about__hero"
      :style="`background-image: linear-gradient(160deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%), url(${imageBase}/Images/about-us/about-hero.jpg)`"
    >
      <div class="about__hero--top">
        <span class="about__hero--tag">Our Story</span>
        <h1 class="white-text title">{{ aboutContent.hero.title }}</h1>
      </div>
    </div>

    <!-- Video / Story -->
    <div class="about__video">
      <h2 class="pink-text about__video--title">{{ aboutContent.video.title_1 }}</h2>
      <div class="about__video--player">
        <iframe
          src="https://customer-2yoll9ob2c366vuz.cloudflarestream.com/a7eae959eb14fbda0dde37df0da73ea6/iframe?muted=true&preload=true&autoplay=true&poster=https%3A%2F%2Fcustomer-2yoll9ob2c366vuz.cloudflarestream.com%2Fa7eae959eb14fbda0dde37df0da73ea6%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
          loading="lazy"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
          allowfullscreen="true"
        />
      </div>
      <div class="about__video--text">
        <p
          v-for="(item, i) in aboutContent.video.body"
          :key="`body_${i}`"
          class="grey-text about__video--body"
        >
          {{ item }}
        </p>
      </div>
    </div>

    <!-- Values -->
    <div class="about__values">
      <h2 class="pink-text">{{ aboutContent.values.title }}</h2>
      <div class="about__values--section">
        <div
          v-for="(item, i) in aboutContent.values.items"
          :key="`value__${i}`"
          class="column"
        >
          <div class="column__icon">
            <img
              loading="lazy"
              :src="staticUrl(`/Images/icons/${item.image}.svg`)"
              alt="logo"
              width="60"
            />
          </div>
          <h4 class="pink-text">{{ item.title }}</h4>
          <p class="grey-text">{{ item.body }}</p>
        </div>
      </div>
    </div>

    <!-- Founders -->
    <div class="about__founders">
      <div class="about__founders--image">
        <img
          loading="lazy"
          :src="staticUrl(`/Images/about-us/${aboutContent.founders.image}.jpeg`)"
          alt="founders"
          width="600"
        />
      </div>
      <div class="about__founders--text">
        <h2 class="pink-text">{{ aboutContent.founders.title }}</h2>
        <p class="grey-text">{{ aboutContent.founders.body }}</p>
      </div>
    </div>

    <!-- Makers -->
    <div class="about__makers">
      <div class="about__makers--top">
        <h2 class="pink-text">{{ aboutContent.makers.title }}</h2>
        <p class="subtitle grey-text">{{ aboutContent.makers.body }}</p>
        <div class="about__makers--grid">
          <div
            v-for="(item, i) in aboutContent.makers.items"
            :key="`maker_${i}`"
            class="slide__wrapper"
          >
            <img
              loading="lazy"
              class="slide__wrapper--image"
              :src="staticUrl(`/Images/about-us/${item.image}`)"
              :alt="item.image"
              width="400"
            />
            <div class="slide__wrapper--info">
              <div class="text">
                <h4 class="pink-text">{{ item.title }}</h4>
                <p class="grey-text">{{ item.body }}</p>
              </div>
              <DynamicButton
                class="button"
                :text="item.button"
                color="pink"
                width="100%"
                @click-event="navigateTo('/search')"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Testimonials -->
    <div class="about__testimonials">
      <h2 class="pink-text title">{{ aboutContent.testimonials.title }}</h2>
      <p class="grey-text subtitle">{{ aboutContent.testimonials.subtitle }}</p>
      <div class="about__testimonials--wrapper">
        <img
          v-for="(icon, i) in aboutContent.testimonials.icons"
          :key="`icon_${i}`"
          loading="lazy"
          class="image"
          :src="staticUrl(`/Images/company-logos/${icon}-logo.svg`)"
          :alt="icon"
        />
      </div>
    </div>

    <!-- Bottom CTA -->
    <div
      class="about__cta"
      :style="`background-image: linear-gradient(160deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.35) 100%), url(${imageBase}/Images/about-us/about-foot.jpg)`"
    >
      <h2 class="white-text">Ready to transform your workplace dining?</h2>
      <DynamicButton
        text="Browse Makers"
        color="white"
        @click-event="navigateTo('/search')"
      />
    </div>
  </section>
</template>

<style lang="scss">
.about {
  margin-top: 5rem;

  // ----- Hero -----
  &__hero {
    width: 100%;
    // Border-box so the % padding stays inside the 100% width, instead
    // of adding to it and pushing the hero past the viewport edge —
    // that was clipping the title on mobile and producing horizontal
    // scroll on the right.
    box-sizing: border-box;
    min-height: 36rem;
    background-position: center;
    background-size: cover;
    color: var(--color-white-1);
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
    padding: 6rem 8%;
    @media (max-width: $tablet) {
      min-height: 28rem;
      padding: 4rem 5%;
      align-items: center;
      justify-content: center;
      text-align: center;
    }
    &--tag {
      display: inline-block;
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: var(--color-white-1);
      background: rgba(255, 255, 255, 0.15);
      backdrop-filter: blur(4px);
      padding: 0.4rem 1rem;
      border-radius: 2rem;
      margin-bottom: 1rem;
    }
    &--top {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      max-width: 36rem;
      @media (max-width: $tablet) {
        align-items: center;
        max-width: 100%;
      }
      .title {
        font-size: 2.8rem;
        line-height: 1.15;
        @media (max-width: $tablet) {
          font-size: 2rem;
        }
      }
    }
  }

  // ----- Video / Story -----
  &__video {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 5rem 5% 4rem;
    max-width: 64rem;
    margin: 0 auto;
    @media (max-width: $tablet) {
      padding: 3rem 5% 2rem;
    }
    &--title {
      font-size: 2rem;
      margin-bottom: 2.5rem;
      text-align: center;
    }
    &--player {
      width: 100%;
      border-radius: 1rem;
      overflow: hidden;
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
      margin-bottom: 3rem;
      @media (max-width: $tablet) {
        border-radius: 0.5rem;
        margin-bottom: 2rem;
      }
      iframe {
        width: 100%;
        height: 36rem;
        display: block;
        @media (max-width: $tablet) {
          height: 15rem;
        }
      }
    }
    &--text {
      max-width: 48rem;
    }
    &--body {
      text-align: center;
      line-height: 1.7;
      margin-bottom: 1.25rem;
      font-size: 1rem;
    }
  }

  // ----- Values -----
  &__values {
    padding: 4rem 5%;
    text-align: center;
    background: var(--color-grey-4);
    border-radius: 0;
    h2 {
      margin-bottom: 0.5rem;
    }
    &--section {
      margin-top: 2.5rem;
      display: flex;
      justify-content: center;
      gap: 2rem;
      @media (max-width: $tablet) {
        flex-direction: column;
        align-items: center;
      }
      .column {
        flex: 1;
        max-width: 22rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 2.5rem 2rem;
        background: var(--color-white-1);
        border-radius: 1rem;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        transition:
          transform 0.25s ease,
          box-shadow 0.25s ease;
        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        }
        @media (max-width: $tablet) {
          max-width: 100%;
          width: 100%;
        }
        &__icon {
          margin-bottom: 1rem;
        }
        h4 {
          margin: 0.75rem 0 0.5rem;
        }
        p {
          line-height: 1.65;
          font-size: 0.9rem;
        }
      }
    }
  }

  // ----- Founders -----
  &__founders {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5rem 5%;
    gap: 4rem;
    @media (max-width: $tablet) {
      flex-direction: column;
      padding: 3rem 5%;
      gap: 2rem;
    }
    &--image {
      border-radius: 1.25rem;
      height: 22rem;
      width: 22rem;
      overflow: hidden;
      flex-shrink: 0;
      box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      @media (max-width: $tablet) {
        height: 18rem;
        width: 18rem;
      }
    }
    &--text {
      max-width: 32rem;
      @media (max-width: $tablet) {
        text-align: center;
      }
      h2 {
        margin-bottom: 1rem;
      }
      p {
        line-height: 1.7;
      }
    }
  }

  // ----- Makers -----
  &__makers {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 4rem 0;
    background: var(--color-grey-4);
    @media (max-width: $tablet) {
      padding: 2.5rem 0;
    }
    &--top {
      padding: 0 5%;
      .subtitle {
        max-width: 40rem;
        margin: 0.5rem auto 2rem;
        text-align: center;
        line-height: 1.6;
      }
      h2 {
        text-align: center;
      }
    }
    &--grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1.5rem;
      margin-top: 1rem;
      @media (max-width: $laptop) {
        grid-template-columns: repeat(2, 1fr);
      }
      @media (max-width: $tablet) {
        grid-template-columns: 1fr;
      }
      .slide__wrapper {
        position: relative;
        border-radius: 1rem;
        background: var(--color-white-1);
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        transition:
          transform 0.25s ease,
          box-shadow 0.25s ease;
        display: flex;
        flex-direction: column;
        min-width: 0;
        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        }
        &--image {
          width: 100%;
          aspect-ratio: 4 / 3;
          object-fit: cover;
          object-position: top;
          display: block;
          border-radius: 1rem 1rem 0 0;
        }
        &--info {
          display: flex;
          flex-direction: column;
          flex: 1;
          padding: 1.25rem;
          justify-content: space-between;
          min-width: 0;
          box-sizing: border-box;
          .text {
            min-width: 0;
            overflow-wrap: break-word;
            word-wrap: break-word;
            h4 {
              margin-bottom: 0.4rem;
            }
            p {
              font-size: 0.6rem;
              line-height: 1.6;
            }
          }
          .button {
            margin-top: 1rem;
            flex-shrink: 0;
            width: 100%;
            box-sizing: border-box;
          }
        }
      }
    }
  }

  // ----- Testimonials -----
  &__testimonials {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 5rem 5% 4rem;
    @media (max-width: $tablet) {
      padding: 3rem 5%;
    }
    .title {
      margin-bottom: 0.5rem;
    }
    .subtitle {
      max-width: 36rem;
      text-align: center;
      margin: 0 auto 2.5rem;
      line-height: 1.6;
    }
    &--wrapper {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      justify-content: center;
      gap: 2rem;
      .image {
        height: 2rem;
        opacity: 0.6;
        transition: opacity 0.25s ease;
        &:hover {
          opacity: 1;
        }
      }
      @media (max-width: $tablet) {
        gap: 1.5rem;
      }
    }
  }

  // ----- Bottom CTA -----
  &__cta {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 5rem 5%;
    background-position: center;
    background-size: cover;
    gap: 1.5rem;
    h2 {
      font-size: 1.75rem;
      max-width: 30rem;
      line-height: 1.3;
      @media (max-width: $tablet) {
        font-size: 1.4rem;
      }
    }
  }
}
</style>
